import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Tooltip } from './Tooltip';
import { cn } from '../lib/cn';

/**
 * The canonical stat card (skill §3): blue icon tile, blue-border+shadow hover,
 * a tooltip describing the metric, and h-full so N cards in a row match heights.
 */
export function StatTile({
  label,
  value,
  icon: Icon,
  tooltip,
  badge,
  caption,
  className,
}: {
  label: string;
  value: ReactNode;
  icon: LucideIcon;
  tooltip: string;
  badge?: ReactNode;
  /**
   * What the number is, in four or five words, under it.
   *
   * A tile is stretched to the height of the tallest thing in its row, so in a
   * row beside a taller card it was a number over a third of a card of
   * nothing. The sentence that was only in the tooltip fills that space and
   * pays for it: a tooltip is invisible until someone suspects there is
   * something to hover.
   */
  caption?: string;
  className?: string;
}) {
  return (
    <Tooltip content={tooltip} className={cn('block w-full', className)}>
      <div className="h-full w-full rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:border-blue-500 hover:shadow-md">
        <div className="flex min-h-[80px] items-start gap-3 p-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50">
            <Icon className="h-5 w-5 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1 text-left">
            {/* Wraps, never truncates: "Businesses scanned" reading
                "BUSINESSES SCAN…" is a worse tile than a two-line label. The
                overflow this file fixes was the value row, not this. */}
            <div className="mb-1 text-xs uppercase tracking-wide text-gray-500">{label}</div>
            {/* flex-wrap: the value and its delta used to be pinned to one line,
                so "11,831" beside "↑ 6.8%" ran out through the right edge of
                the tile on the Search performance row. The delta drops under
                the number instead of leaving the card. */}
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-2xl font-semibold leading-none text-gray-900 tabular-nums">
                {value}
              </span>
              {badge && <span className="text-xs font-medium leading-none text-gray-500">{badge}</span>}
            </div>
            {caption && <p className="my-0 mt-1.5 text-xs leading-snug text-gray-500">{caption}</p>}
          </div>
        </div>
      </div>
    </Tooltip>
  );
}
